import React, {useCallback, useEffect, useState} from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import {DataGrid, GridColDef, GridRenderCellParams} from "@mui/x-data-grid";
import { Delete as DeleteIcon, Edit as EditIcon, CheckCircleOutlineOutlined, CancelOutlined, Add as AddIcon } from "@mui/icons-material";
import {confirmDialog} from "../common/ConfirmDialog";
import {useNotifications} from "../../hooks/NotificationProvider";
import AccessRuleModalForm from "./AccessRuleModalForm";
import AccessRuleModel, {AccessRuleView} from "../../core/api/accessRule/AccessRuleModel";
import {translations} from "../../translations/common";
import ManagersModel, {ManagerData} from "../../core/api/manager/ManagersModel";

interface ModalState {
  ruleId?: string | null;
  show: boolean;
}

const AccessRulesTable: React.FC = () => {
  const [modal, setModal] = useState<ModalState>({ruleId: null, show: false});
  const notifications = useNotifications();
  const [loading, setLoading] = useState<boolean>(true);
  const [rules, setRules] = useState<AccessRuleView[]>([]);
  const [managers, setManagers] = useState<ManagerData[]>([]);

  const loadManagers = useCallback(async () => {
    const managers = await ManagersModel.listAll();
    setManagers(managers);
  }, []);

  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const rules = await AccessRuleModel.listAll();
      setRules(rules);
    } catch (error) {
      console.error("Failed to load rules or managers:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadManagers();
    loadRules();
  }, [loadRules, loadManagers]);

  const handleAddRule = () => {
    setModal({show: true, ruleId: null});
  };

  const handleEditRule = (rule: AccessRuleView) => {
    setModal({show: true, ruleId: rule.id});
  };

  const handleDeleteRule = (rule: AccessRuleView) => {
    confirmDialog({
      title: translations.accessRules.table.confirmations.deleteTitle,
      body: translations.accessRules.table.confirmations.deleteBody,
      action: async () => {
        if (rule.id) {
          await AccessRuleModel.delete(rule.id);
          await loadRules();
          notifications.notify({
            level: "success",
            message: translations.accessRules.table.notifications.deleteSuccess,
          });
        }
      },
      actionName: translations.accessRules.table.confirmations.deleteAction,
    });
  };

  const onModalClose = async () => {
    setModal({show: false});
    await loadRules();
  };

  const columns: GridColDef[] = [
    {
      field: "userId",
      headerName: translations.accessRules.table.columns.userId,
      flex: 1,
      renderCell: (params: GridRenderCellParams<AccessRuleView>) => (
          <Box sx={{whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
            {(() : string => {
              const manager = managers.find(m => m.id === params.row.userId);
              return manager ? `${manager.firstName} ${manager.lastName}` : 'Unknown';
            })()}
          </Box>
      )
    },
    {
      field: "enabled",
      headerName: translations.accessRules.table.columns.enabled,
      width: 100,
      renderCell: (params: GridRenderCellParams<AccessRuleView>) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
          {params.row.enabled ? (
            <CheckCircleOutlineOutlined color="success" />
          ) : (
              <CancelOutlined color="error" />
          )}
        </Box>
      )
    },
    {
      field: "ipAddresses",
      headerName: translations.accessRules.table.columns.ipAddresses,
      flex: 1,
      renderCell: (params: GridRenderCellParams<AccessRuleView>) => (
          <Box sx={{whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
            {params.row.ipAddresses.join(",")}
          </Box>
      )
    },
    {
      field: "browserIds",
      headerName: translations.accessRules.table.columns.browserIds,
      flex: 1,
      renderCell: (params: GridRenderCellParams<AccessRuleView>) => (
          <Box sx={{whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
            {params.row.browserIds.join(",")}
          </Box>
      )
    },
    {
      field: "actions",
      headerName: "",
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams<AccessRuleView>) => {
        const rule = params.row;
        return (
            <Box sx={{display: "flex"}}>
              <Tooltip title={translations.accessRules.table.actions.edit}>
                <IconButton onClick={() => handleEditRule(rule)} size="small">
                  <EditIcon/>
                </IconButton>
              </Tooltip>
              <Tooltip title={translations.accessRules.table.actions.delete}>
                <IconButton onClick={() => handleDeleteRule(rule)} size="small" color="error">
                  <DeleteIcon/>
                </IconButton>
              </Tooltip>
            </Box>
        );
      },
    },
  ];

  return (
      <Box sx={{
        width: "100%",
        height: '100%',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column'

      }}>
        <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#f5f5f5',
              fontWeight: 'bold',
              fontSize: '1.1rem',
              borderBottom: '1px solid #ddd'
            }}
        >
          <Typography variant="h6">{translations.accessRules.table.title}</Typography>
        <IconButton
              onClick={handleAddRule}
          color="primary"
          size="small"
          >
          <AddIcon />
        </IconButton>
        </Box>
        <DataGrid
            rows={rules}
            columns={columns}
            loading={loading}
            initialState={{pagination: {paginationModel: {pageSize: 10, page: 0}}}}
            pageSizeOptions={[10]}
            disableColumnMenu
            disableRowSelectionOnClick
            getRowId={(row) => row.id || `rule-${row.userId}`}
            getRowClassName={(params) => params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row'}
            slots={{
              noRowsOverlay: () => (
                  <Box sx={{p: 2, textAlign: 'center'}}>
                    {translations.accessRules.table.noData}
                  </Box>
              ),
            }}
            sx={{
              border: 'none',
              '& .even-row': {backgroundColor: '#ffffff'},
              '& .odd-row': {backgroundColor: '#efefef'},
              '& .MuiDataGrid-columnHeaders': {backgroundColor: '#bdbdbd'},
              '& .hidden-column': {display: 'none'},
              '& .MuiDataGrid-cell': {borderBottom: 'none'},
              '& .MuiDataGrid-row': {borderBottom: '1px solid', borderColor: 'divider'},
            }}
        />
        {modal.show && (
            <AccessRuleModalForm ruleId={modal.ruleId} close={onModalClose}/>
        )}
      </Box>
  );
};

export default AccessRulesTable;
